import { DC_Domain_Models_Products_ProductType, DC_Repositories_Base_Enumerations_Label } from '@monorepo-types/dc';

import {
  ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_MOVING_OUT,
  ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_SWITCH,
  ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_URGENT,
  ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MAX_URGENT,
  ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MAX,
  OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_MOVING_OUT,
  OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_SWITCH,
  OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_URGENT,
  OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MAX_URGENT,
  OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MAX,
  ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_HIGH,
  ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_LOW,
  ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_GAS,
  ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_WARMTH,
  ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_WATER,
  OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_HIGH,
  OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_LOW,
  OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_GAS,
  OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_WARMTH,
  OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_WATER,
} from './constants';
import { EnergyType, Offer, ContractOption, OfferType, type ProductFilter } from '../types';

type MaxUsageType = 'ELECTRICITY_HIGH' | 'ELECTRICITY_LOW' | 'GAS' | 'WARMTH' | 'WATER';

const COMMODITY_PRODUCT_TYPES = new Set<DC_Domain_Models_Products_ProductType>(['gas', 'electricity']);
const HEATING_PRODUCT_TYPES = new Set<DC_Domain_Models_Products_ProductType>(['warmth', 'warmthEkv']);
const DYNAMIC_OR_HYBRID_DISCOUNT_TYPES = ['dynamic', 'Hybrid'] as const;
const NON_FIXED_DISCOUNT_TYPES = ['dynamic', 'Hybrid', 'variable'] as const;

const byLabel = <T>(label: DC_Repositories_Base_Enumerations_Label, enecoValue: T, oxxioValue: T): T =>
  label === 'eneco' ? enecoValue : oxxioValue;

const isCommodityProduct = (product: Offer['products'][number]): boolean =>
  COMMODITY_PRODUCT_TYPES.has(product.type);

const hasCommodityWithDuration = (offer: Offer | undefined, isZeroDuration: boolean): boolean =>
  !!offer?.products?.some(product => {
    if (!isCommodityProduct(product)) return false;
    const duration = product.contractSpan?.duration;
    return isZeroDuration ? duration === 0 : duration !== 0;
  });

const discountTypeIncludesAny = (discountType: string, types: readonly string[]): boolean =>
  types.some(type => discountType.includes(type));

const isDynamicOrHybridDiscountType = (discountType: string): boolean =>
  discountTypeIncludesAny(discountType, DYNAMIC_OR_HYBRID_DISCOUNT_TYPES);

const isVariableDiscountType = (discountType: string): boolean => discountType.includes('variable');

const isFixedDiscountType = (discountType: string): boolean =>
  !discountTypeIncludesAny(discountType, NON_FIXED_DISCOUNT_TYPES);

export const checkOfferIncludesDynamicOrVariablePricingProduct = (offer?: Offer): OfferType => {
  if (!offer) return OfferType.None;

  if (hasCommodityWithDuration(offer, true)) {
    if (offer.discountType === OfferType.Variable) return OfferType.Variable;
    if (offer.discountType?.includes(OfferType.Dynamic)) return OfferType.Dynamic;
  }

  return OfferType.Fixed;
};

export const checkOfferIncludesDynamicPricingProduct = (offer?: Offer): boolean =>
  hasCommodityWithDuration(offer, true);

/**
 * Maps an offer to the contract-type filter key that reveals it in the product toggle.
 *
 * Single source of truth for this mapping — every caller that pre-selects a contract type must go
 * through it so the keys it emits and the keys {@link filterOffersByType} matches on cannot drift.
 */
export const getProductFilterForOffer = (offer?: Offer): ProductFilter => {
  if (offer?.discountType === OfferType.Variable) return OfferType.Variable;
  if (checkOfferIncludesDynamicPricingProduct(offer)) return OfferType.Dynamic;

  return OfferType.Fixed;
};

/**
 * `productFilter` is typed as `string` because it also arrives straight from the Sitecore
 * `contractTypeRadio` option names via the RadioGroup. Code that produces it should use
 * {@link getProductFilterForOffer} so it is always one of the {@link ProductFilter} keys.
 */
export const filterOffersByType = (offers: Offer[], productFilter: string): Offer[] => {
  return offers.filter(({ discountType }) => {
    if (!discountType) return false;

    if (productFilter === OfferType.Dynamic) return isDynamicOrHybridDiscountType(discountType);
    if (productFilter === OfferType.Variable) return isVariableDiscountType(discountType);
    if (productFilter === OfferType.Fixed) return isFixedDiscountType(discountType);

    return discountType.includes(productFilter);
  });
};

export const checkOfferTypes = (contractOptions: ContractOption[], offers: Offer[]): ContractOption[] =>
  contractOptions.filter(option => getAvailableOfferTypes(offers).some(offerType => offerType?.includes(option.name)));

export const getAvailableOfferTypes = (offers: Offer[]) => {
  const availableOfferTypes: string[] = [];

  offers.forEach(({ discountType }) => {
    if (!discountType) return;

    if (isDynamicOrHybridDiscountType(discountType)) {
      availableOfferTypes.push(OfferType.Dynamic);
    }

    if (isVariableDiscountType(discountType)) {
      availableOfferTypes.push(OfferType.Variable);
    }

    if (isFixedDiscountType(discountType)) {
      availableOfferTypes.push(OfferType.Fixed);
    }
  });

  return [...new Set(availableOfferTypes)];
};

export const checkIfOfferHasTimeOfUseProduct = (offer?: Offer) =>
  !!offer?.products?.some?.(product => product.priceDifferentiation === 'TimeOfUse');

export const checkOfferIncludesHeatingProduct = (offer?: Offer): boolean =>
  !!offer?.products?.some(product => HEATING_PRODUCT_TYPES.has(product.type));

export const checkOfferIncludesNonDynamicPricingProduct = (offer?: Offer): boolean =>
  hasCommodityWithDuration(offer, false) || checkIfOfferHasTimeOfUseProduct(offer);

export const checkOffersIncludesDynamicPricingProduct = (offers: Offer[]): boolean =>
  offers.some(offer => checkOfferIncludesDynamicPricingProduct(offer));

export const checkOffersIncludeVariablePricingProduct = (offer?: Offer): boolean =>
  !!offer?.discountType?.includes(OfferType.Variable);

export type ContractQueryParam = 'tou' | 'fixed' | 'dynamic' | 'hybrid' | 'variable';

export const getContractQueryParam = (offer?: Offer): ContractQueryParam | undefined => {
  if (!offer) return undefined;

  if (checkIfOfferHasTimeOfUseProduct(offer)) return 'tou';

  const discountType = offer.discountType?.toLowerCase() ?? '';
  if (discountType.includes('hybrid')) return 'hybrid';
  if (discountType.includes('dynamic')) return 'dynamic';
  if (discountType.includes('variable')) return 'variable';
  if (/^newenergy(\d+(months?|years?))?$/i.test(offer.discountType ?? '')) return 'fixed';

  return undefined;
};

export const checkAvailabilityOfferType = (offers: Offer[], discountCode?: string): string | undefined =>
  offers.find(offer => offer.type === discountCode)?.type;

export const selectDefaultDiscountCode = (offers: Offer[]): string | undefined =>
  (offers.find(offer => offer.costDiscounts?.cashback || offer.mostPopularChoice) ?? offers[0])?.type;

export const energyTypeIncludes = (energyType: EnergyType, type: EnergyType): boolean =>
  new RegExp(`${type}`, 'i').test(energyType);

export const getMinUrgentContractStartDateInDays = (label: DC_Repositories_Base_Enumerations_Label) =>
  byLabel(
    label,
    ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_URGENT,
    OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_URGENT,
  );

export const getMaxUrgentContractStartDateInDays = (label: DC_Repositories_Base_Enumerations_Label) =>
  byLabel(
    label,
    ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MAX_URGENT,
    OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MAX_URGENT,
  );

export const getMinContractStartDateInDays = (
  isMoving: boolean,
  isUrgent: boolean,
  label: DC_Repositories_Base_Enumerations_Label,
): number => {
  if (isUrgent) return getMinUrgentContractStartDateInDays(label);

  return isMoving
    ? byLabel(
        label,
        ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_MOVING_OUT,
        OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_MOVING_OUT,
      )
    : byLabel(
        label,
        ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_SWITCH,
        OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN_SWITCH,
      );
};

export const getMaxContractStartDateInDays = (label: DC_Repositories_Base_Enumerations_Label) =>
  byLabel(label, ENECO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MAX, OXXIO_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MAX);

const MAX_USAGE_BY_TYPE: Record<MaxUsageType, { eneco: number; oxxio: number }> = {
  ELECTRICITY_HIGH: {
    eneco: ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_HIGH,
    oxxio: OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_HIGH,
  },
  ELECTRICITY_LOW: {
    eneco: ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_LOW,
    oxxio: OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_ELECTRICITY_LOW,
  },
  GAS: {
    eneco: ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_GAS,
    oxxio: OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_GAS,
  },
  WARMTH: {
    eneco: ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_WARMTH,
    oxxio: OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_WARMTH,
  },
  WATER: {
    eneco: ENECO_ENERGY_FLOW_MAX_USAGE_VALUE_WATER,
    oxxio: OXXIO_ENERGY_FLOW_MAX_USAGE_VALUE_WATER,
  },
};

export const getMaxUsageValue = (type: MaxUsageType, label: DC_Repositories_Base_Enumerations_Label): number => {
  const values = MAX_USAGE_BY_TYPE[type];
  return values ? byLabel(label, values.eneco, values.oxxio) : 0;
};

/**
 * Sorts contract options so that 'Vast', 'Variable', 'Dynamic' appear in this order if present.
 */
export const sortContractOptionsByPreferredOrder = (contractOptions: ContractOption[]): ContractOption[] => {
  const contractOrder = ['Vast', 'Variable', 'Dynamic'];
  return [...contractOptions].sort((a, b) => {
    const aLabel = a.label || a.name;
    const bLabel = b.label || b.name;
    const aIndex = contractOrder.indexOf(aLabel);
    const bIndex = contractOrder.indexOf(bLabel);
    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });
};

export const sortOffersBasedOnTimeOfUse = (o1: Offer, o2: Offer) => {
  const o1PriceDiff = o1.contractSpan?.[0]?.priceDifferentiation;
  const o2PriceDiff = o2.contractSpan?.[0]?.priceDifferentiation;

  if (o1PriceDiff === 'TimeOfUse' && o2PriceDiff !== 'TimeOfUse') return -1;
  if (o2PriceDiff === 'TimeOfUse' && o1PriceDiff !== 'TimeOfUse') return 1;

  return 0;
};
